import React, { useState, useRef, useMemo, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import FormInput from '../../../form-input/FormInput/FormInput';
import CustomPicker from './CustomPicker/CustomPicker';
import moment from 'moment';
import UserContext, { UserProfileContext } from '../../../../../../common/utils/getContext';
import { useWidgetTranslates } from '../../../../../../common/utils/utils';
import css from './DatePicker.module.scss';
import classnames from 'classnames';
import { generateCompStylers } from '../../../../../../common/utils/styles';
import { getDefaultVariation } from '../../../helpers';
import { getDatePlaceholder, getFormattedDate } from '../../initData';

const DatePickerForm = (props) => {
    const {
        variation,
        compStyle,
        title,
        handlers = {},
        attribute,
        value,
        showTimeSelect,
        isTime,
        placeholder,
        execludeYear,
        disabled
    } = props;
    const { stylers, classes } = generateCompStylers(compStyle);
    const { stylersCalendar, classesCalendar } = generateCompStylers(compStyle?.calendar, 'Calendar');
    const profileContext = useContext(UserProfileContext) || {};
    const variationStyle = variation || getDefaultVariation(profileContext, title);
    const pickerElement = useRef();
    const { settings, lang } = useContext(UserContext);
    const transl = useWidgetTranslates('form');

    const dateFormat = useCallback(
        (dating) => {
            if (isTime) {
                const time = dating.locale('en');
                switch (settings?.time_format) {
                    case '0':
                        return time.format('h:mm a');
                    case '1':
                        return time.format('h:mm A');
                    case '2':
                        return time.format('HH:mm');
                    default:
                        return time.format('HH:mm');
                }
            } else {
                return getFormattedDate(dating, execludeYear, settings, lang);
            }
        },
        [isTime, lang, execludeYear, settings]
    );
    const placeholderText = useMemo(() => {
        if (placeholder) return placeholder;
        if (isTime) {
            switch (settings?.time_format) {
                case '0':
                    return '00:00 pm';
                case '1':
                    return '00:00 PM';
                case '2':
                    return '00:00';
                default:
                    return '00:00';
            }
        } else {
            return getDatePlaceholder(settings, execludeYear, transl);
        }
    }, [isTime, placeholder, settings, execludeYear, transl]);
    const [focused, setFocused] = useState(false);
    const openCalendar = () => {
        if (disabled) {
            return;
        }
        setFocused(true);
        !!handlers?.onFocus &&
            handlers?.onFocus(
                attribute,
                value
                    ? moment(value, showTimeSelect ? 'DD.MM.yyyy HH:mm' : 'DD.MM.yyyy').format(
                          showTimeSelect ? 'yyyy-MM-DD HH:mm' : 'yyyy-MM-DD'
                      )
                    : null
            );
    };
    const formatValue = useMemo(() => {
        if (!props?.value) return null;
        const toMoment = moment(props.value, showTimeSelect ? 'yyyy-MM-DD HH:mm' : 'yyyy-MM-DD');
        return toMoment.isValid() ? dateFormat(toMoment) : null;
    }, [dateFormat, props?.value, showTimeSelect]);
    return (
        <>
            <div
                ref={pickerElement}
                style={stylers.wrapper}
                className={classnames(css.formGroup, css[variationStyle], classes.wrapper || {})}
            >
                <FormInput
                    {...props}
                    handlers={{
                        ...(props.handlers || {}),
                        onClick: () => openCalendar()
                    }}
                    value={formatValue}
                    placeholder={placeholderText}
                    noWrapper={true}
                    readonly={true}
                    compStyle={{
                        ...props.compStyle,
                        calendar: {
                            ...props.compStyle?.calendar,
                            button: {
                                width: '24px',
                                height: '24px',
                                className: classesCalendar.button,
                                ...stylersCalendar.button
                            },
                            icon: {
                                width: '24px',
                                height: '24px',
                                fill: focused ? 'var(--blueColor)' : 'var(--greyExtraDarkColor)',
                                className: classnames(classesCalendar.icon),
                                ...stylersCalendar.icon
                            }
                        }
                    }}
                    buttons={{
                        ...props.buttons,
                        calendar: { icon: isTime ? 'clock' : 'calendar-icon-filled-new' }
                    }}
                />
            </div>
            {focused ? <CustomPicker {...props} pickerElement={pickerElement} setFocused={setFocused} /> : null}
        </>
    );
};
DatePickerForm.propTypes = {
    compStyle: PropTypes.object,
    styles: PropTypes.object,
    handlers: PropTypes.object,
    attribute: PropTypes.string,
    value: PropTypes.string,
    showTimeSelect: PropTypes.bool,
    isTime: PropTypes.bool,
    placeholder: PropTypes.node
};
export default DatePickerForm;
